import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError } from '@/api/client.types';
import { createLegacyKuduToken } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

interface RawGetDynamicImageUrlResponse {
  dynamic_image_url?: string | null;
  dynamicImageUrl?: string | null;
  original_url?: string | null;
  originalUrl?: string | null;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

function normalizeDynamicImageUrl(
  response: RawGetDynamicImageUrlResponse | null | undefined,
) {
  const value = response?.dynamic_image_url ?? response?.dynamicImageUrl;
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

export function toGsBucketPath(storageObjectPath: string) {
  const normalizedPath = storageObjectPath.trim().replace(/^\/+/, '');
  return `/gs/koob-polymer.appspot.com/${normalizedPath}`;
}

export function useGetDynamicImageUrl() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getIdToken, user } = useAuth();
  const [error, setError] = useState<ApiError | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getDynamicImageUrl = useCallback(
    async (storageObjectPath: string) => {
      if (!user) {
        throw createDataError('You must be signed in to generate a dynamic image URL.');
      }

      setIsLoading(true);
      setError(null);

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      try {
        const response = await client.invokeKuduMethod<
          RawGetDynamicImageUrlResponse,
          string
        >('GetDynamicImageURL', toGsBucketPath(storageObjectPath));

        return normalizeDynamicImageUrl(response);
      } catch (requestError) {
        const normalizedError =
          typeof requestError === 'object' &&
          requestError !== null &&
          'kind' in requestError
            ? (requestError as ApiError)
            : createDataError(
                requestError instanceof Error
                  ? requestError.message
                  : 'Unable to generate a dynamic image URL.',
              );

        setError(normalizedError);
        throw normalizedError;
      } finally {
        setIsLoading(false);
      }
    },
    [getIdToken, location.hash, location.pathname, location.search, navigate, user],
  );

  return {
    error,
    getDynamicImageUrl,
    isLoading,
    resetError: () => {
      setError(null);
    },
  };
}
